import React from "react";
import { useRouter } from "next/router";
import { Menu, Flex } from "@mantine/core";
import { JSONSchemaFaker } from "json-schema-faker";
import { event as gaEvent } from "nextjs-google-analytics";
import toast from "react-hot-toast";
import { CgChevronDown } from "react-icons/cg";
import { FaRandom } from "react-icons/fa";
import { MdFilterListAlt, MdOutlineCompareArrows } from "react-icons/md";
import { SiJsonwebtokens } from "react-icons/si";
import { VscSearchFuzzy, VscJson, VscGroupByRefType } from "react-icons/vsc";
import { useTranslation } from "../../../hooks/useTranslation";
import { jsonToContent } from "../../../lib/utils/jsonAdapter";
import useFile from "../../../store/useFile";
import useJson from "../../../store/useJson";
import { useModal } from "../../../store/useModal";
import { StyledToolElement } from "./styles";

export const ToolsMenu = () => {
  const setVisible = useModal(state => state.setVisible);
  const getJson = useJson(state => state.getJson);
  const setContents = useFile(state => state.setContents);
  const getFormat = useFile(state => state.getFormat);
  const { t } = useTranslation();
  const router = useRouter();

  const randomizeData = async () => {
    try {
      // 获取JSON数据
      const jsonData = getJson();

      // 检查JSON数据是否有效
      if (!jsonData || typeof jsonData !== "string" || jsonData.trim() === "") {
        toast.error(t("Invalid JSON!"));
        return;
      }

      // generate json schema
      const { run } = await import("json_typegen_wasm");
      const jsonSchema = run(
        "Root",
        jsonData,
        JSON.stringify({
          output_mode: "json_schema",
        })
      );

      // 验证生成的schema是否有效
      if (!jsonSchema || typeof jsonSchema !== "string" || jsonSchema.trim() === "") {
        toast.error(t("Invalid Schema"));
        return;
      }

      let parsedSchema;
      try {
        parsedSchema = JSON.parse(jsonSchema);
      } catch (parseError) {
        toast.error(t("Invalid Schema"));
        return;
      }

      // generate random data
      const randomJson = JSONSchemaFaker.generate(parsedSchema);
      const contents = await jsonToContent(JSON.stringify(randomJson, null, 2), getFormat());
      setContents({ contents });

      gaEvent("randomize_data");
    } catch (error) {
      console.error(error);
      toast.error(t("Error"));
    }
  };

  const goToJsonToYamlConverter = () => {
    router.push("/converter/json-to-yaml");
    gaEvent("go_to_json_yaml_converter");
  };

  return (
    <Menu shadow="md" withArrow>
      <Menu.Target>
        <StyledToolElement onClick={() => gaEvent("show_tools_menu")}>
          <Flex align="center" gap={3}>
            {t("Tools")} <CgChevronDown />
          </Flex>
        </StyledToolElement>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          fz={12}
          leftSection={<VscSearchFuzzy />}
          onClick={() => {
            setVisible("JQModal", true);
            gaEvent("open_jq_modal");
          }}
        >
          {t("JSON Query (jq)")}
        </Menu.Item>
        <Menu.Item
          fz={12}
          leftSection={<VscJson />}
          onClick={() => {
            setVisible("SchemaModal", true);
            gaEvent("open_schema_modal");
          }}
        >
          {t("JSON Schema")}
        </Menu.Item>
        <Menu.Item
          fz={12}
          leftSection={<MdFilterListAlt />}
          onClick={() => {
            setVisible("JPathModal", true);
            gaEvent("open_json_path_modal");
          }}
        >
          {t("JSON Path")}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          fz={12}
          leftSection={<SiJsonwebtokens />}
          onClick={() => {
            setVisible("JWTModal", true);
            gaEvent("open_jwt_modal");
          }}
        >
          {t("Decode JWT")}
        </Menu.Item>
        <Menu.Item
          fz={12}
          leftSection={<VscGroupByRefType />}
          onClick={() => {
            setVisible("TypeModal", true);
            gaEvent("open_type_modal");
          }}
        >
          {t("Generate Type")}
        </Menu.Item>
        <Menu.Item fz={12} leftSection={<FaRandom />} onClick={randomizeData}>
          {t("Randomize Data")}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          fz={12}
          leftSection={<MdOutlineCompareArrows />}
          onClick={goToJsonToYamlConverter}
        >
          {t("JSON to YAML Converter")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
